import React, { useRef } from "react";
import { useTranslation } from "next-i18next";
import { useLazyQuery } from "@apollo/client";
import { CHECK_SUBDOMAIN_QUERY } from "../../../../graphql/queries/checkSubDomain";
import { useRouter } from "next/router";
import { ClickOutsideToClose } from "../../../../components/TailwindControls/shared/ClickOutsideToClose";
import { InstanceAddFinished } from "./InstanceAddFinished";
import { InstanceAddProcessing } from "./InstanceAddProcessing";
import { InstanceAddStepTwoFormWrapper } from "./InstanceAddStepTwoFormWrapper";
import { InstanceAddStepOneFormWrapper } from "./InstanceAddStepOneFormWrapper";
import { InstanceAddStepThreeFormWrapper } from "./InstanceAddStepThreeFormWrapper";
import {
  detailsValidationSchema,
  formStepperArray,
  getLaunchUrl,
  loadingArray,
} from "./sharedData";
import { ICRMInstance } from "../../../../models/Accounts";
import { SupportedApps } from "../../../../models/shared";
import { observer } from "mobx-react-lite";
import { handleDomainCheck } from "../Shared/handleDomainCheck";

export const ConnectedInstanceAddForm = observer(
  ({
    instance,
    loading,
    steps,
    currentSubdomain,
    editMode = false,
    processCompleted,
    renderFullPage = false,
    instances,
    selectedApp,
    handleAppSelection,
    handleSubmit,
    setSteps,
    onOutsideClick = () => {},
    handleButtonClose = () => {},
  }: {
    instance?: Partial<ICRMInstance>;
    loading: boolean;
    steps: number;
    currentSubdomain: string;
    editMode?: boolean;
    processCompleted?: boolean;
    renderFullPage?: boolean;
    instances: ICRMInstance[];
    selectedApp: string[];
    handleAppSelection: (app: string) => void;
    handleSubmit: (input: Partial<ICRMInstance>) => void;
    setSteps: (value: ((prevState: number) => number) | number) => void;
    onOutsideClick?: (value: boolean) => void;
    handleButtonClose?: () => void;
  }) => {
    const internalInstance: Partial<ICRMInstance> = {
      ...{
        instanceAdmins: [],
        subdomain: "",
        description: "",
        name: "",
      },
      ...instance,
    };
    const router = useRouter();
    const { t } = useTranslation(["instances", "common"]);
    const [instanceAdmins, setAdmin] = React.useState<string[]>(
      internalInstance.instanceAdmins || []
    );
    const wrapperRef = useRef(null);
    ClickOutsideToClose(wrapperRef, (value: boolean) => onOutsideClick(value));
    const [adminsError, setAdminsError] = React.useState<string>("");
    const [formArray, setFormArray] = React.useState<Record<string, any>>({});

    const [checksubdomain] = useLazyQuery(CHECK_SUBDOMAIN_QUERY, {
      fetchPolicy: "no-cache",
      context: {
        headers: {
          vrynopath: SupportedApps.accounts,
        },
      },
    });

    if (processCompleted) {
      const instanceId = instance?.id || formArray?.id;
      return (
        <InstanceAddFinished
          href={getLaunchUrl(currentSubdomain, router?.locale)}
          instanceAdmins={formArray?.instanceAdmins}
          instanceId={instanceId}
          onButtonClose={handleButtonClose}
        />
      );
    }

    if (loading && !processCompleted) {
      return (
        <InstanceAddProcessing
          renderFullPage={renderFullPage}
          messages={loadingArray}
        />
      );
    }
    if (steps === 0) {
      return (
        <InstanceAddStepOneFormWrapper
          renderFullPage={renderFullPage}
          t={t}
          FormStepperArray={formStepperArray}
          steps={steps}
          internalInstance={internalInstance}
          formArray={formArray}
          instancesExist={instances.length !== 0}
          setFormArray={setFormArray}
          setSteps={setSteps}
          handleButtonClose={handleButtonClose}
          onOutsideClick={onOutsideClick}
        />
      );
    }
    if (steps === 1) {
      return (
        <InstanceAddStepTwoFormWrapper
          t={t}
          renderFullPage={renderFullPage}
          FormStepperArray={formStepperArray}
          steps={steps}
          internalInstance={internalInstance}
          formArray={formArray}
          instanceAdmins={instanceAdmins}
          editMode={editMode}
          adminsError={adminsError}
          setAdmin={setAdmin}
          setAdminsError={setAdminsError}
          setFormArray={setFormArray}
          setSteps={setSteps}
          onOutsideClick={onOutsideClick}
          detailsValidationSchema={detailsValidationSchema(t)}
          handleDomainCheck={(values) =>
            handleDomainCheck(values, checksubdomain, t)
          }
        />
      );
    }
    return (
      <InstanceAddStepThreeFormWrapper
        renderFullPage={renderFullPage}
        FormStepperArray={formStepperArray}
        steps={steps}
        selectedApp={selectedApp}
        internalInstance={internalInstance}
        formArray={formArray}
        loading={loading}
        handleSubmit={handleSubmit}
        setSteps={setSteps}
        onOutsideClick={onOutsideClick}
        handleAppSelection={handleAppSelection}
        detailsValidationSchema={detailsValidationSchema(t)}
      />
    );
  }
);
